import type { DeathStats, HuntPhase } from '@kenocyber/game-domain';

/**
 * Whether the run being published to React owes it a death, and nothing else.
 *
 * ## Why this is a function and not four lines inside the scene
 *
 * It was four lines inside the scene, and seventeen tests asserted that those
 * lines existed. They all passed while a player coming home from a 3x3 stood on
 * a dead ship with no button: the lines were there, and the ORDER between them
 * and `hunt:restarted` was wrong. Assertions over source text cannot fail for
 * that.
 *
 * Pulled out here, the decision runs in a test. What stays in the scene is where
 * the decision is CALLED - inside `pushSnapshot`, so the death always travels in
 * the same synchronous batch as the snapshot that carries the dead phase - and
 * that placement is one line, checked by `hunt-death-announce.test.ts` and
 * proven end to end by `e2e/pvp-skins/gate-v24d4-return.spec.ts`.
 *
 * ## The rule
 *
 * A run that is dead, whose numbers are readable, and whose death has not been
 * announced yet, must be announced - once. Everything else is silence, so every
 * caller can be unconditional.
 *
 * ## And the latch belongs to the announcement
 *
 * The version this replaced set `deadNotified = true` and only afterwards looked
 * for the stats and the cause, publishing nothing when either was missing. That
 * is a permanent soft-lock from one missing number: the flag says the death was
 * announced, nothing ever tries again, and the top bar reads "Abatida" for the
 * rest of the run with no overlay to act on. Here the latch is part of the
 * result, and it only turns true alongside a message.
 */
export interface PublishedRun {
  readonly phase: HuntPhase;
  readonly deathStats: DeathStats | null;
  readonly deathCause: 'boss-breach' | 'mob-leaks' | null;
}

export interface DeathAnnouncement {
  /** The `hunt:death` payload to publish, or null when nothing is owed. */
  readonly announce: {
    readonly stats: DeathStats;
    readonly cause: 'boss-breach' | 'mob-leaks';
  } | null;
  /** The latch AFTER this decision. True only once something was published. */
  readonly announced: boolean;
}

export function deathAnnouncement(run: PublishedRun, announced: boolean): DeathAnnouncement {
  if (announced || run.phase !== 'dead') {
    return { announce: null, announced };
  }
  const { deathStats: stats, deathCause: cause } = run;
  if (!stats || !cause) {
    return { announce: null, announced: false };
  }
  return { announce: { stats, cause }, announced: true };
}
